/**
 * Pure helpers for the /confirm-reset interstitial. The email (built in send-auth-email)
 * links to `/confirm-reset#token_hash=<hashed_token>&type=recovery`. The token rides in
 * the URL FRAGMENT (not the query string) so it is never sent to the server and can't land
 * in app/server/CDN access logs (audit advisory). The page does NOT verify on load — it
 * verifies (verifyOtp) only on the user's click, so email security scanners can't consume
 * the one-time token by prefetching the link.
 */
export interface ConfirmResetParams {
  tokenHash: string | null;
  type: string;
  valid: boolean;
}

/** Parse the interstitial params. Accepts legacy `token` as a fallback for `token_hash`. */
export function parseConfirmResetParams(
  sp: URLSearchParams,
): ConfirmResetParams {
  const tokenHash = sp.get("token_hash") || sp.get("token");
  const type = sp.get("type") || "recovery";
  return { tokenHash, type, valid: Boolean(tokenHash) };
}

/** Parse from the URL fragment (e.g. window.location.hash) — the canonical transport. */
export function parseConfirmResetHash(hash: string): ConfirmResetParams {
  return parseConfirmResetParams(
    new URLSearchParams((hash || "").replace(/^#/, "")),
  );
}

/** Canonical interstitial path (mirrors the link built server-side in send-auth-email). */
export function buildConfirmResetPath(
  tokenHash: string,
  type = "recovery",
): string {
  return `/confirm-reset#token_hash=${encodeURIComponent(tokenHash)}&type=${encodeURIComponent(type)}`;
}
